<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import NotasAPI from 'dashboard/api/fandata/notas';
import Avatar from 'next/avatar/Avatar.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

// Notas do TKT-xxx (T-232).
//
// A nota do ATENDIMENTO é o que o agente escreve todo dia; a nota do CONTATO é rara e ficou
// atrás de um botão. Por isso esta veio para cima, logo abaixo dos dados do ticket.
//
// Não existe tabela nova: a nota do atendimento é a MENSAGEM PRIVADA que já existe, e escrever
// aqui é exatamente escrever lá. Quem quiser continuar usando a caixa de sempre com "Mensagem
// Privada" marcada segue fazendo, e a nota aparece nos dois lugares. Ver notas_controller.rb.
const props = defineProps({
  conversationId: { type: [Number, String], required: true },
});

const { t } = useI18n();
const store = useStore();
const currentUser = useMapGetter('getCurrentUser');
const currentChat = useMapGetter('getSelectedChat');

const notas = ref([]);
const carregando = ref(false);
const salvando = ref(false);
const texto = ref('');
const escrevendo = ref(false);

const carregar = async () => {
  if (!props.conversationId) return;
  carregando.value = true;
  try {
    const { data } = await NotasAPI.get(props.conversationId);
    notas.value = data.notas || [];
  } catch (e) {
    // A lista falhar não pode derrubar o painel inteiro: o bloco fica vazio e o atendimento segue.
    notas.value = [];
  } finally {
    carregando.value = false;
  }
};

watch(() => props.conversationId, carregar, { immediate: true });

// A linha do tempo é a fonte: quando uma nota nova é escrita na caixa de sempre, ela entra por
// lá e este bloco precisa acompanhar. Contar as privadas do store é barato e pega o caso comum
// sem inventar um canal de eventos só para isto.
const privadasNaTela = computed(
  () => (currentChat.value?.messages || []).filter(m => m.private).length
);
watch(privadasNaTela, () => carregar());

const podeSalvar = computed(() => texto.value.trim().length > 0);

const salvar = async () => {
  if (!podeSalvar.value || salvando.value) return;
  salvando.value = true;
  try {
    await store.dispatch('createPendingMessageAndSend', {
      conversationId: props.conversationId,
      message: texto.value.trim(),
      private: true,
      sender: currentUser.value,
    });
    texto.value = '';
    escrevendo.value = false;
    await carregar();
  } catch (e) {
    useAlert(t('FANDATA_NOTAS.ERRO'));
  } finally {
    salvando.value = false;
  }
};

const quando = iso => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${dd}/${mm} ${hh}:${mi}`;
};
</script>

<template>
  <div class="flex flex-col gap-2">
    <div v-if="escrevendo" class="flex flex-col gap-2">
      <textarea
        v-model="texto"
        rows="3"
        class="w-full px-2 py-1.5 text-sm rounded-lg resize-y bg-n-alpha-black2 text-n-slate-12 border border-n-weak focus:outline-none focus:border-n-brand"
        :placeholder="t('FANDATA_NOTAS.PLACEHOLDER')"
      />
      <div class="flex items-center gap-2">
        <NextButton
          sm
          :label="t('FANDATA_NOTAS.SALVAR')"
          :is-loading="salvando"
          :disabled="!podeSalvar"
          @click="salvar"
        />
        <NextButton
          sm
          faded
          slate
          :label="t('FANDATA_NOTAS.CANCELAR')"
          @click="
            escrevendo = false;
            texto = '';
          "
        />
      </div>
    </div>
    <NextButton
      v-else
      sm
      faded
      slate
      icon="i-lucide-plus"
      :label="t('FANDATA_NOTAS.ADICIONAR')"
      @click="escrevendo = true"
    />

    <span v-if="carregando" class="text-xs text-n-slate-10">
      {{ t('FANDATA_NOTAS.CARREGANDO') }}
    </span>

    <span
      v-else-if="!notas.length"
      class="text-xs text-n-slate-10"
      data-fandata-notas-vazio
    >
      {{ t('FANDATA_NOTAS.VAZIO') }}
    </span>

    <ul v-else class="flex flex-col gap-2 m-0 list-none">
      <li
        v-for="nota in notas"
        :key="nota.id"
        class="flex flex-col gap-1 p-2 rounded-lg bg-n-alpha-black2"
      >
        <div class="flex items-center gap-1.5 min-w-0">
          <Avatar
            v-if="nota.autor"
            :name="nota.autor.nome || ''"
            :src="nota.autor.avatar"
            :size="16"
          />
          <span class="text-xs truncate text-n-slate-11">
            {{ nota.autor ? nota.autor.nome : t('FANDATA_NOTAS.PELO_SISTEMA') }}
          </span>
          <span class="ml-auto text-xs shrink-0 text-n-slate-10">
            {{ quando(nota.criada_em) }}
          </span>
        </div>
        <p class="m-0 text-sm whitespace-pre-wrap text-n-slate-12">
          {{ nota.conteudo }}
        </p>
      </li>
    </ul>
  </div>
</template>
